import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  CssBaseline,
  Dialog,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MovieIcon from '@mui/icons-material/Movie';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import { publicFilesAPI } from '../api';
import { getApiBaseUrl } from '../utils/apiConfig';
import { fileMatchesShareKey } from '../utils/publicShareUrl';
import {
  compareFiles,
  formatFileDate,
  groupFilesByFolder,
  persistViewMode,
  readStoredViewMode,
} from '../utils/fileListView';

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function isImageName(name) {
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(name || '');
}

function isVideoName(name) {
  return /\.(mp4|m4v|webm|ogv|ogg|mov)$/i.test(name || '');
}

function previewNeighborFiles(list, index) {
  if (!Array.isArray(list) || index < 0) return [];
  const out = [];
  if (index > 0) out.push(list[index - 1]);
  if (list[index]) out.push(list[index]);
  if (index + 1 < list.length) out.push(list[index + 1]);
  return out;
}

function publicMediaUrl(apiBaseUrl, path) {
  if (!path || !apiBaseUrl) return '';
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith('/api/')) {
    const origin = apiBaseUrl.replace(/\/api\/?$/, '');
    return `${origin}${path}`;
  }
  return path;
}

function FolderThumb({ apiBaseUrl, file, fileUrl }) {
  const thumbSrc = publicMediaUrl(apiBaseUrl, file.thumbnailUrl) || fileUrl(file);
  const fullSrc = fileUrl(file);
  const [src, setSrc] = useState(thumbSrc);
  const [usedFallback, setUsedFallback] = useState(!file.thumbnailUrl);

  useEffect(() => {
    setSrc(thumbSrc);
    setUsedFallback(!file.thumbnailUrl);
  }, [thumbSrc, file.thumbnailUrl]);

  return (
    <img
      src={src}
      alt={file.fileName}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (!usedFallback && fullSrc && fullSrc !== src) {
          setUsedFallback(true);
          setSrc(fullSrc);
        }
      }}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      draggable={false}
    />
  );
}

function buildTree(folders) {
  const byParent = new Map();
  for (const f of folders) {
    const key = f.parentId || null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(f);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }
  return byParent;
}

function buildFolderPath(folders, folderId) {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const chain = [];
  let cur = byId.get(folderId);
  while (cur) {
    chain.unshift(cur);
    cur = cur.parentId ? byId.get(cur.parentId) : null;
  }
  return chain;
}

function CenteredMessage({ children }) {
  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      {children}
    </Box>
  );
}

export default function PublicFolderView({
  shareToken,
  initialFolderId = null,
  initialFileKeys = [],
  openGallery = false,
  initialShowSubfolders = false,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [folders, setFolders] = useState([]);
  const [rootId, setRootId] = useState(null);
  const [ownerName, setOwnerName] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewList, setPreviewList] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [viewMode, setViewMode] = useState(
    initialShowSubfolders ? 'thumbs' : readStoredViewMode
  );
  const [showSubfolders, setShowSubfolders] = useState(!!initialShowSubfolders);
  const [sortBy, setSortBy] = useState('sortIndex');
  const [sortDir, setSortDir] = useState('asc');
  const openedFromLink = useRef(false);
  const prevLoadingFiles = useRef(true);
  const touchStartX = useRef(null);

  useEffect(() => {
    (async () => {
      setApiBaseUrl((await getApiBaseUrl()).replace(/\/$/, ''));
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    publicFilesAPI.getFolderTree(shareToken)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setFolders(data.folders || []);
        setRootId(data.rootId || null);
        setOwnerName(data.ownerName || '');
        const folderIds = new Set((data.folders || []).map((f) => f.id));
        const startId = initialFolderId && folderIds.has(initialFolderId)
          ? initialFolderId
          : (data.rootId || null);
        setSelectedFolderId(startId);
        const expandedIds = new Set(data.rootId ? [data.rootId] : []);
        if (startId) {
          const byId = new Map((data.folders || []).map((f) => [f.id, f]));
          let cur = byId.get(startId);
          while (cur) {
            expandedIds.add(cur.id);
            cur = cur.parentId ? byId.get(cur.parentId) : null;
          }
        }
        setExpanded(expandedIds);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.status === 404
            ? 'This share link is not valid (any more). Ask the owner for a new one.'
            : e?.response?.data?.error || 'Could not open this shared folder.'
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shareToken, initialFolderId]);

  const loadFiles = useCallback(async (folderId) => {
    if (!folderId) {
      setFiles([]);
      return;
    }
    setLoadingFiles(true);
    try {
      const includeSubfolders = viewMode === 'thumbs' && showSubfolders;
      const res = await publicFilesAPI.getFiles(shareToken, folderId, { includeSubfolders });
      setFiles(res.data || []);
    } catch (e) {
      console.error('Failed to load public files', e);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [shareToken, viewMode, showSubfolders]);

  useEffect(() => {
    loadFiles(selectedFolderId);
  }, [selectedFolderId, loadFiles]);

  const tree = useMemo(() => buildTree(folders), [folders]);
  const path = useMemo(
    () => buildFolderPath(folders, selectedFolderId),
    [folders, selectedFolderId]
  );
  const displayedFiles = useMemo(() => {
    if (viewMode !== 'details') return files;
    const list = [...files];
    list.sort((a, b) => compareFiles(a, b, sortBy, sortDir));
    return list;
  }, [files, viewMode, sortBy, sortDir]);
  const includeSubfolders = viewMode === 'thumbs' && showSubfolders;
  const thumbGroups = useMemo(
    () => (includeSubfolders
      ? groupFilesByFolder(files, selectedFolderId)
      : [{ folderId: selectedFolderId, folderName: '', isRoot: true, files }]),
    [files, includeSubfolders, selectedFolderId]
  );

  useEffect(() => {
    persistViewMode(viewMode);
  }, [viewMode]);

  const handleSortColumn = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDir(column === 'uploadedAt' || column === 'fileSize' ? 'desc' : 'asc');
  };

  const openPreview = useCallback((file, list = displayedFiles) => {
    const idx = list.findIndex(
      (f) => f.storedName === file.storedName && (!file.folderId || f.folderId === file.folderId)
    );
    setPreviewList(list);
    setPreviewIndex(idx >= 0 ? idx : 0);
    setPreview(file);
  }, [displayedFiles]);

  const closePreview = useCallback(() => {
    setPreview(null);
    setPreviewList([]);
    setPreviewIndex(0);
  }, []);

  const goPreview = useCallback((delta) => {
    setPreviewIndex((current) => {
      const next = current + delta;
      if (next < 0 || next >= previewList.length) return current;
      setPreview(previewList[next]);
      return next;
    });
  }, [previewList]);

  useEffect(() => {
    const justFinishedLoading = prevLoadingFiles.current && !loadingFiles;
    prevLoadingFiles.current = loadingFiles;
    if (!justFinishedLoading || openedFromLink.current) return;
    if (!selectedFolderId) return;
    if (initialFolderId && selectedFolderId !== initialFolderId) return;
    const hasSpecificFiles = initialFileKeys && initialFileKeys.length > 0;
    if (!hasSpecificFiles && !openGallery) return;
    const ordered = hasSpecificFiles
      ? initialFileKeys
          .map((key) => files.find((f) => fileMatchesShareKey(f.storedName, key)))
          .filter(Boolean)
      : files;
    openedFromLink.current = true;
    if (ordered.length > 0) {
      openPreview(ordered[0], ordered);
    }
  }, [files, loadingFiles, initialFileKeys, initialFolderId, selectedFolderId, openGallery, openPreview]);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goPreview(-1);
      if (e.key === 'ArrowRight') goPreview(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, goPreview]);

  const fileUrl = useCallback(
    (file, { download = false } = {}) => {
      if (!apiBaseUrl) return '';
      const url = `${apiBaseUrl}/public/folders/${shareToken}/folders/${file.folderId}/files/${encodeURIComponent(file.storedName)}`;
      return download ? `${url}?download=1` : url;
    },
    [apiBaseUrl, shareToken]
  );

  const handleDownload = (file) => {
    const url = fileUrl(file, { download: true });
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = file.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderNode = (node, depth) => {
    const children = tree.get(node.id) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(node.id);
    const isSelected = selectedFolderId === node.id;
    return (
      <Box key={node.id}>
        <Box
          onClick={() => setSelectedFolderId(node.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: 1 + depth * 3,
            pr: 0.5,
            py: 0.35,
            cursor: 'pointer',
            bgcolor: isSelected ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) toggleExpanded(node.id);
            }}
            sx={{ width: 28, height: 28, visibility: hasChildren ? 'visible' : 'hidden' }}
            aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
          >
            {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
          {isSelected || isExpanded ? (
            <FolderOpenIcon fontSize="small" sx={{ mx: 0.5, color: isSelected ? 'primary.main' : 'inherit' }} />
          ) : (
            <FolderIcon fontSize="small" sx={{ mx: 0.5 }} />
          )}
          <Typography
            variant="body2"
            noWrap
            sx={{
              flex: 1,
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? 'primary.main' : 'inherit',
            }}
          >
            {node.name}
          </Typography>
        </Box>
        {isExpanded && children.map((c) => renderNode(c, depth + 1))}
      </Box>
    );
  };

  if (loading) {
    return (
      <CenteredMessage>
        <CircularProgress />
      </CenteredMessage>
    );
  }

  if (error) {
    return (
      <CenteredMessage>
        <Alert severity="error" sx={{ maxWidth: 480 }}>{error}</Alert>
      </CenteredMessage>
    );
  }

  const roots = tree.get(null) || [];
  const rootFolder = folders.find((f) => f.id === rootId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar>
          <PublicIcon sx={{ mr: 1.5 }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {rootFolder?.name || 'Shared folder'}
            </Typography>
            <Typography variant="caption" noWrap sx={{ opacity: 0.85 }}>
              Shared by {ownerName} · read-only
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
            py: 1,
          }}
        >
          {roots.map((r) => renderNode(r, 0))}
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Toolbar variant="dense" sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 56 }}>
            <Breadcrumbs separator="›" sx={{ flex: 1, minWidth: 0 }} aria-label="folder breadcrumb">
              {path.map((folder, i) => {
                const isLast = i === path.length - 1;
                if (isLast) {
                  return (
                    <Typography key={folder.id} color="text.primary" noWrap sx={{ fontWeight: 600 }}>
                      {folder.name}
                    </Typography>
                  );
                }
                return (
                  <Link
                    key={folder.id}
                    component="button"
                    variant="body2"
                    underline="hover"
                    color="inherit"
                    onClick={() => setSelectedFolderId(folder.id)}
                  >
                    {folder.name}
                  </Link>
                );
              })}
            </Breadcrumbs>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={viewMode}
              onChange={(_, next) => {
                if (next) setViewMode(next);
              }}
              aria-label="Folder view mode"
            >
              <ToggleButton value="thumbs" aria-label="Thumbnail view" title="Thumbnails">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="details" aria-label="Details view" title="Details">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            {viewMode === 'thumbs' && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showSubfolders}
                    onChange={(e) => setShowSubfolders(e.target.checked)}
                  />
                }
                label="Show subfolders"
                sx={{
                  ml: 1,
                  whiteSpace: 'nowrap',
                  '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                }}
              />
            )}
          </Toolbar>

          <Box sx={{ flex: 1, overflow: 'auto', p: viewMode === 'details' && files.length > 0 ? 0 : 2 }}>
            {loadingFiles ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : files.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 6 }}>
                <InsertDriveFileIcon sx={{ fontSize: 72, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This folder is empty.
                </Typography>
              </Box>
            ) : viewMode === 'details' ? (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={sortBy === 'sortIndex' ? sortDir : false} sx={{ width: 88 }}>
                        <TableSortLabel
                          active={sortBy === 'sortIndex'}
                          direction={sortBy === 'sortIndex' ? sortDir : 'asc'}
                          onClick={() => handleSortColumn('sortIndex')}
                        >
                          Index
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={sortBy === 'fileName' ? sortDir : false}>
                        <TableSortLabel
                          active={sortBy === 'fileName'}
                          direction={sortBy === 'fileName' ? sortDir : 'asc'}
                          onClick={() => handleSortColumn('fileName')}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={sortBy === 'fileSize' ? sortDir : false} sx={{ width: 120 }}>
                        <TableSortLabel
                          active={sortBy === 'fileSize'}
                          direction={sortBy === 'fileSize' ? sortDir : 'desc'}
                          onClick={() => handleSortColumn('fileSize')}
                        >
                          Size
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={sortBy === 'uploadedAt' ? sortDir : false} sx={{ width: 200 }}>
                        <TableSortLabel
                          active={sortBy === 'uploadedAt'}
                          direction={sortBy === 'uploadedAt' ? sortDir : 'desc'}
                          onClick={() => handleSortColumn('uploadedAt')}
                        >
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedFiles.map((file) => (
                      <TableRow
                        key={file.storedName}
                        hover
                        onClick={() => openPreview(file)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{file.sortIndex || ''}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            {isImageName(file.fileName) ? (
                              <Box sx={{ width: 32, height: 32, borderRadius: 0.5, overflow: 'hidden', flexShrink: 0, bgcolor: 'grey.100' }}>
                                <FolderThumb apiBaseUrl={apiBaseUrl} file={file} fileUrl={fileUrl} />
                              </Box>
                            ) : isVideoName(file.fileName) ? (
                              <MovieIcon fontSize="small" color="action" />
                            ) : (
                              <InsertDriveFileIcon fontSize="small" color="action" />
                            )}
                            <Tooltip title={file.fileName}>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                {file.fileName}
                              </Typography>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell>{formatFileSize(file.fileSize)}</TableCell>
                        <TableCell>{formatFileDate(file.uploadedAt)}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Preview">
                            <IconButton size="small" onClick={() => openPreview(file)}>
                              <FullscreenIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => handleDownload(file)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box>
                {thumbGroups.map((group) => (
                  <Box key={group.folderId || 'root'} sx={{ mb: includeSubfolders && !group.isRoot ? 3 : 0 }}>
                    {!group.isRoot && group.folderName ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          width: 'auto',
                          mx: -2,
                          px: 2,
                          my: 1.5,
                        }}
                      >
                        <Box sx={{ flex: 1, height: 2, bgcolor: '#000' }} />
                        <Typography
                          sx={{
                            color: '#000',
                            fontWeight: 700,
                            fontSize: '1.05rem',
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {group.folderName}
                        </Typography>
                        <Box sx={{ flex: 1, height: 2, bgcolor: '#000' }} />
                      </Box>
                    ) : null}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: 2,
                      }}
                    >
                {group.files.map((file) => {
                  const isImage = isImageName(file.fileName);
                  const isVideo = isVideoName(file.fileName);
                  return (
                    <Paper
                      key={`${file.folderId}:${file.storedName}`}
                      variant="outlined"
                      sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                    >
                      <Box
                        sx={{
                          height: 140,
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          overflow: 'hidden',
                        }}
                        onClick={() => openPreview(file)}
                      >
                        {isImage ? (
                          <FolderThumb apiBaseUrl={apiBaseUrl} file={file} fileUrl={fileUrl} />
                        ) : isVideo ? (
                          <MovieIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                        ) : (
                          <InsertDriveFileIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                        )}
                      </Box>
                      <Box sx={{ p: 1 }}>
                        <Tooltip title={file.fileName}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                            {file.fileName}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          {file.sortIndex ? `#${file.sortIndex} · ` : ''}
                          {formatFileSize(file.fileSize)}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        sx={{ borderTop: '1px solid', borderColor: 'divider', justifyContent: 'space-around' }}
                      >
                        <Tooltip title="Preview">
                          <IconButton size="small" onClick={() => openPreview(file)}>
                            <FullscreenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton size="small" onClick={() => handleDownload(file)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  );
                })}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={!!preview}
        onClose={closePreview}
        fullScreen
        PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.95)', display: 'flex', flexDirection: 'column', position: 'relative' } }}
      >
        {preview && (
          <>
            {includeSubfolders && preview.folderName && preview.folderId && preview.folderId !== selectedFolderId ? (
              <Typography
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 0,
                  right: 0,
                  zIndex: 3,
                  textAlign: 'center',
                  color: 'white',
                  fontSize: '2.25rem',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  pointerEvents: 'none',
                  px: 10,
                  textShadow: '0 2px 8px rgba(0,0,0,0.85)',
                }}
              >
                {preview.folderName}
              </Typography>
            ) : null}
            <Toolbar
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.6)',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
                <Typography variant="subtitle1" noWrap>
                  {preview.fileName}
                </Typography>
                {previewList.length > 1 && (
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {previewIndex + 1} / {previewList.length}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                <IconButton color="inherit" onClick={() => handleDownload(preview)} title="Download">
                  <DownloadIcon />
                </IconButton>
                <IconButton color="inherit" onClick={closePreview} title="Close">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Toolbar>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                p: 2,
                position: 'relative',
              }}
              onTouchStart={(e) => {
                touchStartX.current = e.changedTouches[0]?.clientX ?? null;
              }}
              onTouchEnd={(e) => {
                if (touchStartX.current == null) return;
                const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
                touchStartX.current = null;
                if (dx > 50) goPreview(-1);
                if (dx < -50) goPreview(1);
              }}
            >
              {previewList.length > 1 && (
                <IconButton
                  color="inherit"
                  onClick={() => goPreview(-1)}
                  disabled={previewIndex <= 0}
                  title="Previous"
                  sx={{
                    position: 'absolute',
                    left: 8,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.35)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
                  }}
                >
                  <ChevronLeftIcon fontSize="large" />
                </IconButton>
              )}
              {previewNeighborFiles(previewList, previewIndex)
                .filter((item) => isImageName(item.fileName))
                .map((item) => {
                  const isCurrent = item.storedName === preview.storedName;
                  return (
                    <img
                      key={item.storedName}
                      src={fileUrl(item)}
                      alt={isCurrent ? item.fileName : ''}
                      draggable={false}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        display: isCurrent && isImageName(preview.fileName) ? 'block' : 'none',
                      }}
                    />
                  );
                })}
              {isVideoName(preview.fileName) ? (
                <video
                  key={preview.storedName}
                  src={fileUrl(preview)}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                >
                  Your browser cannot play this video. Download it instead.
                </video>
              ) : !isImageName(preview.fileName) ? (
                <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 480 }}>
                  <InsertDriveFileIcon sx={{ fontSize: 96, color: 'text.secondary' }} />
                  <Typography variant="h6" sx={{ mt: 2, wordBreak: 'break-all' }}>
                    {preview.fileName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {formatFileSize(preview.fileSize)}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    sx={{ mt: 3 }}
                    onClick={() => handleDownload(preview)}
                  >
                    Download
                  </Button>
                </Paper>
              ) : null}
              {previewList.length > 1 && (
                <IconButton
                  color="inherit"
                  onClick={() => goPreview(1)}
                  disabled={previewIndex >= previewList.length - 1}
                  title="Next"
                  sx={{
                    position: 'absolute',
                    right: 8,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.35)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
                  }}
                >
                  <ChevronRightIcon fontSize="large" />
                </IconButton>
              )}
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
}
